/**
 * Coups de pouce — Attendu 657 « Problèmes mettant en jeu des distances à un point ».
 *
 * Une entrée par `generator_key` (cf. gen_657.js). Contrat d'une entrée :
 *   pages : 2 pages MAXIMUM, chacune { titre, lignes?, svg? }.
 *           Page 1 « La règle »   : les phrases du mini-cours.
 *           Page 2 « Un exemple » : la figure qui les illustre.
 *
 * Règle absolue : l'aide porte sur d'AUTRES valeurs que celles que le générateur
 * peut tirer, sinon elle donne la réponse. Chaque coup de pouce est autonome —
 * les formats sont indépendants et un exercice peut ne jamais être donné.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure Ex. 1 : la corde tendue trace le cercle ──
// Corde de 12 m : le générateur tire L ∈ {3,4,5,6,7,8,10}, donc hors de portée.
// La corde est dessinée tendue vers la droite, exactement de la longueur du
// rayon : c'est ce qui fait comprendre que le cercle est la trace de son
// extrémité, sans avoir besoin de la faire tourner.
const SVG_CORDE_CERCLE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 212" role="img"
     aria-label="Une corde de 12 m attachée à un piquet trace un cercle ; un objet à 8 m est atteint, un à 12 m tout juste, un à 16 m hors de portée">
  <circle cx="110" cy="105" r="72" stroke="#1D9E75" fill="none" stroke-width="2" stroke-dasharray="5 4"/>

  <line x1="110" y1="105" x2="182" y2="105" stroke="#8B4513" stroke-width="3"/>

  <circle cx="110" cy="105" r="5" fill="#8B4513"/>
  <text x="100" y="119" text-anchor="end" font-size="12.5" font-weight="700" fill="#8B4513">P</text>

  <circle cx="65" cy="89" r="4.5" fill="#1D9E75"/>
  <circle cx="156" cy="50" r="4.5" fill="#F97316"/>
  <circle cx="189" cy="160" r="4.5" fill="#E24B4A"/>

  <circle cx="202" cy="66" r="4.5" fill="#1D9E75"/>
  <text x="214" y="70" font-size="11.5" fill="#334155">8 m &lt; corde  →  atteint</text>
  <circle cx="202" cy="101" r="4.5" fill="#F97316"/>
  <text x="214" y="105" font-size="11.5" fill="#334155">12 m = corde  →  tout juste</text>
  <circle cx="202" cy="136" r="4.5" fill="#E24B4A"/>
  <text x="214" y="140" font-size="11.5" fill="#334155">16 m &gt; corde  →  n'atteint pas</text>

  <text x="110" y="202" text-anchor="middle" font-size="12" font-weight="700" fill="#8B4513">corde = 12 m</text>
</svg>`.trim();

// ── Figure Ex. 3 : les deux cas qui demandent un calcul ──
// Le régime « distance maximale depuis le piquet » n'est pas illustré : la
// réponse EST la longueur de la corde, il n'y a rien à montrer. Restent
// l'addition (depuis un mur) et la soustraction (ce qui manque), qu'un schéma
// linéaire rend immédiats. Valeurs 12 m / 7 m / 20 m : le générateur tire
// L ∈ [3..10], d ∈ [1..5] et PM = L + [1..6], donc aucune n'est atteignable.
const SVG_DEUX_CALCULS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 132" role="img"
     aria-label="Sur une même ligne : un mur, le piquet à 7 m, la corde de 12 m, et l'objet M à 20 m du piquet. On atteint 19 m depuis le mur, et il manque 8 m pour atteindre M.">
  <path d="M26 30 L26 24 L140 24 L140 30" fill="none" stroke="#F97316" stroke-width="1.4"/>
  <text x="83" y="18" text-anchor="middle" font-size="11" font-weight="700" fill="#F97316">19 m</text>

  <line x1="22" y1="40" x2="22" y2="92" stroke="#7a7a7a" stroke-width="6"/>
  <text x="22" y="106" text-anchor="middle" font-size="10" fill="#555">mur</text>

  <line x1="26" y1="66" x2="188" y2="66" stroke="#0f172a" stroke-width="2"/>
  <line x1="68" y1="66" x2="140" y2="66" stroke="#378ADD" stroke-width="4"/>
  <line x1="140" y1="66" x2="188" y2="66" stroke="#E24B4A" stroke-width="2" stroke-dasharray="4 3"/>
  <circle cx="68" cy="66" r="4" fill="#8B4513"/>
  <text x="68" y="58" text-anchor="middle" font-size="11" font-weight="700" fill="#8B4513">P</text>
  <circle cx="188" cy="66" r="4" fill="#E24B4A"/>
  <text x="188" y="58" text-anchor="middle" font-size="11" font-weight="700" fill="#E24B4A">M</text>
  <text x="47" y="82" text-anchor="middle" font-size="10.5" fill="#475569">7 m</text>
  <text x="104" y="82" text-anchor="middle" font-size="10.5" font-weight="700" fill="#378ADD">12 m</text>
  <text x="164" y="82" text-anchor="middle" font-size="10.5" font-weight="700" fill="#E24B4A">8 m</text>

  <path d="M68 92 L68 98 L188 98 L188 92" fill="none" stroke="#94a3b8" stroke-width="1.4"/>
  <text x="128" y="112" text-anchor="middle" font-size="11" fill="#475569">20 m</text>

  <text x="206" y="50" font-size="10.5" fill="#64748b">depuis le mur :</text>
  <text x="206" y="66" font-size="12.5" font-weight="700" fill="#F97316">7 + 12 = 19 m</text>
  <text x="206" y="92" font-size="10.5" fill="#64748b">pour atteindre M :</text>
  <text x="206" y="108" font-size="12.5" font-weight="700" fill="#E24B4A">20 − 12 = 8 m</text>
</svg>`.trim();

// ── Figure Ex. 2 : la zone que l'exercice ne trace pas ──
// La figure de l'exercice place les objets SANS dessiner le cercle, alors que
// trois de ses six familles d'affirmations nomment « la zone atteignable ».
// L'aide montre donc à quoi cette zone ressemble — sur une autre configuration,
// et sans comptage : l'élève doit toujours comparer ses propres distances.
// Corde de 15 m : le générateur tire L ∈ [3..8], donc hors de portée.
const SVG_ZONE_ATTEIGNABLE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 200" role="img"
     aria-label="Un piquet avec une corde de 15 m délimite un disque ; un objet à 9 m est dedans, un objet à 21 m est dehors">
  <circle cx="110" cy="100" r="75" fill="#1D9E75" fill-opacity="0.09" stroke="#1D9E75" stroke-width="2" stroke-dasharray="5 4"/>
  <line x1="110" y1="100" x2="185" y2="100" stroke="#8B4513" stroke-width="2.5"/>
  <circle cx="110" cy="100" r="5" fill="#8B4513"/>
  <text x="100" y="114" text-anchor="end" font-size="12.5" font-weight="700" fill="#8B4513">P</text>
  <text x="147" y="93" text-anchor="middle" font-size="10.5" font-weight="700" fill="#8B4513">15 m</text>

  <circle cx="68" cy="85" r="4.5" fill="#1D9E75"/>
  <circle cx="201" cy="152" r="4.5" fill="#E24B4A"/>

  <circle cx="222" cy="70" r="4.5" fill="#1D9E75"/>
  <text x="234" y="74" font-size="11.5" fill="#334155">9 m &lt; 15 m  →  atteint</text>
  <circle cx="222" cy="105" r="4.5" fill="#E24B4A"/>
  <text x="234" y="109" font-size="11.5" fill="#334155">21 m &gt; 15 m  →  hors de portée</text>

  <text x="110" y="192" text-anchor="middle" font-size="11.5" font-weight="700" fill="#1D9E75">la zone atteignable</text>
</svg>`.trim();

// ── Figure Ex. 4 : deux cercles, deux positions ──
// 7 m et 9 m : le générateur tire ses deux distances dans [2..5], donc hors de
// portée. Les deux intersections sont marquées : c'est la réponse à la phrase
// « il peut y avoir plusieurs positions possibles » que l'énoncé laisse en
// suspens. Coordonnées calculées, pas estimées — chaque intersection est bien
// à 63 px de A et 81 px de B.
const SVG_DEUX_CERCLES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 204" role="img"
     aria-label="Deux cercles sécants, l'un de rayon 7 m centré en A, l'autre de rayon 9 m centré en B, se coupent en deux points">
  <circle cx="130" cy="100" r="63" fill="none" stroke="#378ADD" stroke-width="2"/>
  <circle cx="230" cy="100" r="81" fill="none" stroke="#E24B4A" stroke-width="2"/>

  <line x1="130" y1="100" x2="67" y2="100" stroke="#378ADD" stroke-width="2"/>
  <text x="98" y="93" text-anchor="middle" font-size="10.5" font-weight="700" fill="#378ADD">7 m</text>
  <line x1="230" y1="100" x2="300" y2="60" stroke="#E24B4A" stroke-width="2"/>
  <text x="272" y="72" text-anchor="middle" font-size="10.5" font-weight="700" fill="#E24B4A">9 m</text>

  <circle cx="130" cy="100" r="4" fill="#0f172a"/>
  <text x="124" y="114" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">A</text>
  <circle cx="230" cy="100" r="4" fill="#0f172a"/>
  <text x="238" y="114" font-size="12.5" font-weight="700" fill="#0f172a">B</text>

  <circle cx="167" cy="49" r="6" fill="#F97316"/>
  <circle cx="167" cy="151" r="6" fill="#F97316"/>
  <text x="167" y="38" text-anchor="middle" font-size="11" font-weight="700" fill="#F97316">?</text>
  <text x="167" y="172" text-anchor="middle" font-size="11" font-weight="700" fill="#F97316">?</text>

  <text x="190" y="196" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">deux positions possibles</text>
</svg>`.trim();

// ── Figure Ex. 5 : le va-et-vient carte ↔ réel ──
// Même forme que les tables de conversion de gen_653 et gen_655 : deux cases,
// une flèche dans chaque sens, l'opération sur la flèche. L'élève retrouve un
// visuel connu. Échelle « 1 cm pour 25 m » et longueur 12 cm : le générateur
// tire k ∈ {2,5,10,20,50,100} et des longueurs ≤ 10, donc hors de portée.
const SVG_ECHELLE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 172" role="img"
     aria-label="À l'échelle 1 cm pour 25 m, on multiplie par 25 pour passer de la carte au réel et on divise par 25 dans l'autre sens">
  <text x="190" y="22" text-anchor="middle" font-size="13" font-weight="700" fill="#0f172a">échelle : 1 cm pour 25 m</text>

  <g fill="#EFF6FF" stroke="#1E3A8A" stroke-width="1.5">
    <rect x="70" y="58" width="90" height="38" rx="6"/>
    <rect x="220" y="58" width="90" height="38" rx="6"/>
  </g>
  <g font-size="14" font-weight="700" fill="#1E3A8A" text-anchor="middle">
    <text x="115" y="82">carte</text>
    <text x="265" y="82">réel</text>
  </g>

  <line x1="166" y1="50" x2="206" y2="50" stroke="#F97316" stroke-width="1.8"/>
  <polygon points="214,50 202,45 202,55" fill="#F97316"/>
  <text x="190" y="42" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">× 25</text>

  <line x1="214" y1="106" x2="174" y2="106" stroke="#64748b" stroke-width="1.8"/>
  <polygon points="166,106 178,101 178,111" fill="#64748b"/>
  <text x="190" y="124" text-anchor="middle" font-size="12" font-weight="700" fill="#64748b">÷ 25</text>

  <text x="190" y="158" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">12 cm sur la carte  →  12 × 25 = 300 m</text>
</svg>`.trim();

const CP_657 = {
    zone_atteignable_qcm_brouteur: {
        pages: [
            {
                titre: 'La règle',
                // La ligne 3 est indispensable : les deux premières options du QCM
                // ne se distinguent que par « avec de la marge » contre
                // « exactement à l'extrémité de la corde ».
                lignes: [
                    'La corde trace un **cercle** autour du piquet : l\'animal atteint tout ce qui est **dedans**.',
                    'Compare la **distance à l\'objet** à la **longueur de la corde**.',
                    'Plus **courte** → atteint avec de la marge. **Égale** → atteint tout juste. Plus **longue** → n\'atteint pas.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_CORDE_CERCLE },
        ],
    },

    zone_figure_vf_brouteur: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Compare chaque distance à la **longueur de la corde**.',
                    'Plus **petite** → l\'animal atteint l\'objet, il est dans la zone. Plus **grande** → hors de portée.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_ZONE_ATTEIGNABLE },
        ],
    },

    distance_max_saisie_brouteur: {
        pages: [
            {
                titre: 'La règle',
                // Une ligne par sous-régime du générateur : la corde seule, le
                // départ d'un mur (addition), l'objet hors de portée (soustraction).
                // Sans lettres — P et M sont tirés au hasard.
                lignes: [
                    'La corde fixe la distance maximale depuis le piquet : c\'est **sa longueur**.',
                    'Pour partir d\'un **mur**, **ajoute** la distance du mur au piquet à la longueur de la corde.',
                    'Pour savoir ce qui **manque**, **retire** la longueur de la corde à la distance de l\'objet.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_DEUX_CALCULS },
        ],
    },

    tresor_deux_cercles_programme: {
        pages: [
            {
                titre: 'La règle',
                // Une seule ligne : le format est déjà guidé par trois consignes
                // de tâches. L'aide dit l'outil à employer et le geste final,
                // la figure montre pourquoi il y a deux positions.
                lignes: [
                    'Utilise le **compas virtuel** pour tracer tes cercles, puis touche leurs **deux points d\'intersection**.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_DEUX_CERCLES },
        ],
    },

    echelle_conversion_saisie: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    '« 1 cm pour k m » veut dire : **1 cm sur la carte** représente **k mètres en vrai**.',
                    'De la carte vers le réel, **multiplie** par k. Du réel vers la carte, **divise** par k.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_ECHELLE },
        ],
    },
};

export default CP_657;
